import api from './api';

// Marketplace stage 1b — the provider-managed public posting of a board.
// A listing is the board's shared page; variants are the products (SKUs)
// built on it — SB-01, SB-02, SB-03 share a board and differ in housing —
// each with its own images, documents, specs and hero. Provider/admin calls
// live under /device-boards/:id/...; the anonymous marketing site reads
// /public/catalog and file bytes from /files/:id.

export interface SpecRow {
  group: string | null;
  label: string;
  value: string;
}

export interface BoardListing {
  id: number;
  board_id: number;
  slug: string;
  headline: string | null;
  description: string | null;
  specs: SpecRow[];
  published: boolean;
  published_at: string | null;
  hero_asset_id: number | null;
  image_count: number;
  document_count: number;
  variant_count: number;
  created_at: string;
  updated_at: string;
}

export type ListingInput = Partial<Pick<BoardListing, 'slug' | 'headline' | 'description' | 'specs' | 'hero_asset_id'>>;

export type VariantStatus = 'available' | 'coming-soon' | 'discontinued';

export const VARIANT_STATUS_LABELS: Record<VariantStatus, string> = {
  available: 'Available',
  'coming-soon': 'Coming soon',
  discontinued: 'Discontinued',
};

export interface BoardVariant {
  id: number;
  board_id: number;
  name: string;
  sku: string;
  slug: string;
  headline: string | null;
  description: string | null;
  specs: SpecRow[];
  status: VariantStatus;
  hero_asset_id: number | null;
  sort_order: number;
  image_count?: number;
  document_count?: number;
}

export type VariantInput = Partial<Pick<BoardVariant, 'name' | 'sku' | 'slug' | 'headline' | 'description' | 'specs' | 'status' | 'hero_asset_id' | 'sort_order'>>;

export interface BoardAsset {
  id: number;
  board_id: number;
  variant_id: number | null;
  kind: 'image' | 'document';
  title: string | null;
  file_name: string;
  content_type: string;
  size_bytes: number;
  sort_order: number;
  created_at?: string;
}

export const IMAGE_ACCEPT = 'image/png,image/jpeg,image/webp';
export const DOCUMENT_ACCEPT = 'application/pdf';

const apiBase = () => (import.meta.env.VITE_API_URL || '/api').replace(/\/$/, '');

/** Authenticated preview URL (works before publishing); fetched with the bearer header by AssetImage. */
export function assetPreviewUrl(boardId: number, assetId: number): string {
  return `${apiBase()}/device-boards/${boardId}/assets/${assetId}/content`;
}

/** Anonymous URL — only resolves once the listing is published. */
export function publicFileUrl(assetId: number): string {
  return `${apiBase()}/files/${assetId}`;
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

// ── provider / admin ────────────────────────────────────────────────────────

export async function getListing(boardId: number): Promise<{ listing: BoardListing | null; variants: BoardVariant[]; assets: BoardAsset[] }> {
  const response = await api.get(`/device-boards/${boardId}/listing`);
  return response.data;
}

export async function updateListing(boardId: number, data: ListingInput): Promise<BoardListing> {
  const response = await api.put<BoardListing>(`/device-boards/${boardId}/listing`, data);
  return response.data;
}

export async function publishListing(boardId: number, published: boolean): Promise<BoardListing> {
  const response = await api.post<BoardListing>(`/device-boards/${boardId}/listing/${published ? 'publish' : 'unpublish'}`);
  return response.data;
}

export async function createVariant(boardId: number, data: VariantInput): Promise<BoardVariant> {
  const response = await api.post<BoardVariant>(`/device-boards/${boardId}/variants`, data);
  return response.data;
}

export async function updateVariant(boardId: number, variantId: number, data: VariantInput): Promise<BoardVariant> {
  const response = await api.put<BoardVariant>(`/device-boards/${boardId}/variants/${variantId}`, data);
  return response.data;
}

export async function reorderVariants(boardId: number, ids: number[]): Promise<BoardVariant[]> {
  const response = await api.put<{ variants: BoardVariant[] }>(`/device-boards/${boardId}/variants/reorder`, { ids });
  return response.data.variants;
}

export async function deleteVariant(boardId: number, variantId: number): Promise<void> {
  await api.delete(`/device-boards/${boardId}/variants/${variantId}`);
}

export async function uploadAsset(
  boardId: number,
  file: File,
  options: { title?: string; variantId?: number | null } = {}
): Promise<BoardAsset> {
  const form = new FormData();
  form.append('file', file, file.name);
  if (options.title) form.append('title', options.title);
  if (options.variantId) form.append('variant_id', String(options.variantId));
  const response = await api.post<BoardAsset>(`/device-boards/${boardId}/assets`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
}

export async function updateAsset(boardId: number, assetId: number, data: { title?: string | null; variant_id?: number | null }): Promise<BoardAsset> {
  const response = await api.put<BoardAsset>(`/device-boards/${boardId}/assets/${assetId}`, data);
  return response.data;
}

export async function reorderAssets(boardId: number, ids: number[]): Promise<BoardAsset[]> {
  const response = await api.put<{ assets: BoardAsset[] }>(`/device-boards/${boardId}/assets/reorder`, { ids });
  return response.data.assets;
}

export async function deleteAsset(boardId: number, assetId: number): Promise<void> {
  await api.delete(`/device-boards/${boardId}/assets/${assetId}`);
}

// ── public ──────────────────────────────────────────────────────────────────

export interface PublicVariantSummary {
  id: number;
  name: string;
  sku: string;
  slug: string;
  headline: string | null;
  status: VariantStatus;
  hero_asset_id: number | null;
}

export interface PublicListingSummary {
  slug: string;
  headline: string | null;
  hero_asset_id: number | null;
  published_at: string;
  board_id: number;
  board: string;
  display_name: string | null;
  capabilities: string[];
  /** Allowed values per contract field, e.g. 'doors.reader.technology' → ['prox', 'nfc']. */
  supported: Record<string, string[]>;
  default_num_doors: number;
  default_power_type: string;
  provider_name: string;
  provider_slug: string;
  provider_website: string | null;
  image_count: number;
  document_count: number;
  variants: PublicVariantSummary[];
}

export interface PublicVariant extends PublicVariantSummary {
  description: string | null;
  specs: SpecRow[];
  sort_order: number;
}

export interface PublicListing extends Omit<PublicListingSummary, 'image_count' | 'document_count' | 'variants'> {
  description: string | null;
  specs: SpecRow[];
  features: Record<string, boolean>;
  supported: Record<string, string[]>;
  provider_support_email: string | null;
  variants: PublicVariant[];
  assets: BoardAsset[];
}

/** A hardware provider as the catalog page shows it: profile + published boards. */
export interface PublicProvider {
  name: string;
  slug: string;
  website: string | null;
  tagline: string | null;
  about: string | null;
  support_email: string | null;
  has_logo: boolean;
  listings: PublicListingSummary[];
}

export async function getPublicCatalog(): Promise<PublicProvider[]> {
  const response = await api.get<{ providers: PublicProvider[] }>('/public/catalog');
  return response.data.providers;
}

export async function getPublicListing(providerSlug: string, boardSlug: string): Promise<PublicListing> {
  const response = await api.get<PublicListing>(`/public/catalog/${providerSlug}/${boardSlug}`);
  return response.data;
}

export function publicListingPath(providerSlug: string, boardSlug: string, variantSlug?: string): string {
  return `/product/${providerSlug}/${boardSlug}${variantSlug ? `/${variantSlug}` : ''}`;
}
